// GenericCrudPage.tsx

// Import React
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Import commun
import { AddButton, GenericTable, SecondaryButton } from '../../';
import type { Action } from '../GenericTable/GenericTable';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faAngleLeft,
	faArrowLeft,
	faArrowRight,
	faFileExcel,
	faSpinner,
	faList,
} from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface CrudItem {
	id: number;
	label?: string;
	name?: string;
}

interface QuoteVersion {
	id: number;
	versionIndex: number;
	status: string;
}

type FetchResult<T> =
	| T[]
	| {
			data: T[];
			meta: { page: number; limit: number; total: number; pages: number };
	  };

type ListQueryState = {
	page: number;
	limit: number;
	sortField?: string;
	sortOrder?: 'ASC' | 'DESC';
	filters: Record<string, string>;
};

// Lit l'état du tableau dans l'URL. La clé isole plusieurs cartes sur une même page.
function readListQuery(queryKey: string): ListQueryState {
	const params = new URLSearchParams(window.location.search);
	const prefix = `${queryKey}.`;
	const page = Number(params.get(`${prefix}page`));
	const limit = Number(params.get(`${prefix}limit`));
	const sortField = params.get(`${prefix}sort`) || undefined;
	const order = params.get(`${prefix}order`);
	const filters: Record<string, string> = {};
	const filterPrefix = `${prefix}f.`;
	params.forEach((value, key) => {
		if (key.startsWith(filterPrefix) && value) {
			filters[key.slice(filterPrefix.length)] = value;
		}
	});
	return {
		page: Number.isFinite(page) && page > 0 ? page : 1,
		limit: Number.isFinite(limit) && limit > 0 ? limit : 25,
		sortField,
		sortOrder: order === 'ASC' || order === 'DESC' ? order : undefined,
		filters,
	};
}

// Remplace l'entrée courante, sans empiler un retour par frappe.
function writeListQuery(queryKey: string, state: ListQueryState) {
	const params = new URLSearchParams(window.location.search);
	const prefix = `${queryKey}.`;
	Array.from(params.keys()).forEach((key) => {
		if (key.startsWith(prefix)) params.delete(key);
	});
	if (state.page !== 1) params.set(`${prefix}page`, String(state.page));
	if (state.limit !== 25) params.set(`${prefix}limit`, String(state.limit));
	if (state.sortField) params.set(`${prefix}sort`, state.sortField);
	if (state.sortOrder) params.set(`${prefix}order`, state.sortOrder);
	Object.entries(state.filters).forEach(([key, value]) => {
		if (value) params.set(`${prefix}f.${key}`, value);
	});
	const next = params.toString();
	const current = window.location.search.replace(/^\?/, '');
	if (next === current) return;
	const path = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash}`;
	window.history.replaceState(window.history.state, '', path);
}

interface CrudCardProps<T extends CrudItem> {
	/** Clé stable des paramètres d'URL de ce tableau */
	queryKey: string;
	title: string;
	fetchData: (
		token: string,
		options?: {
			page?: number;
			limit?: number;
			sort?: string;
			order?: 'ASC' | 'DESC';
			filters?: Record<string, string>;
		}
	) => Promise<FetchResult<T>>;
	deleteItem?: (id: number, token: string) => Promise<void>;
	tableColumns: {
		label: string;
		render: (item: T) => React.ReactNode;
		linkUrl?: (item: T) => string;
		field?: string;
		/** Infobulle affichée au survol du libellé de colonne */
		headerTooltip?: string;
		/** Infobulle native affichée au survol de la cellule */
		cellTitle?: (item: T) => string | undefined;
		/** Valeur brute utilisée pour l'export CSV (optionnel). Si absent, on tente de coercer `render` si c'est un primitif. */
		exportValue?: (item: T) => string | number | null | undefined;
	}[];
	addButtonLabel?: string;
	getEditPath?: (id: number) => string;
	getManagePath?: (id: number) => string;
	getManageQuoteVersionPath?: (id: number) => string;
	getManageItemPath?: (item: T) => string;
	getAddPath?: () => string;
	getReturnPath?: () => string;
	returnLabel?: string;
	returnIcon?: IconProp;
	emptyMessage?: string;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	icon?: IconProp;
	returnInvert?: boolean;
	className?: string;
	extraActions?: Action<T>[];
	/** Colonnes supplémentaires incluses uniquement dans l'export CSV (non affichées dans le tableau) */
	exportOnlyColumns?: {
		label: string;
		exportValue: (item: T) => string | number | null | undefined;
	}[];
	/** Affiche un bouton d'export CSV. Défaut : true */
	exportable?: boolean;
	/** Nom du fichier CSV téléchargé (sans extension). Défaut : title */
	exportFileName?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
}

export function GenericCrudCard<T extends CrudItem>({
	queryKey,
	title,
	fetchData,
	deleteItem,
	tableColumns,
	addButtonLabel = 'Ajouter',
	getEditPath,
	getAddPath,
	getManagePath,
	getManageQuoteVersionPath,
	getManageItemPath,
	getReturnPath,
	emptyMessage,
	size,
	icon = faList,
	returnLabel = 'Retour',
	returnIcon = faAngleLeft,
	returnInvert = false,
	className,
	extraActions = [],
	exportable = true,
	exportFileName,
	exportOnlyColumns = [],
	showIcon = true,
}: CrudCardProps<T>) {
	const [items, setItems] = useState<T[]>([]);
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();

	const initialQuery = readListQuery(queryKey);
	const [page, setPage] = useState(initialQuery.page);
	const [limit, setLimit] = useState(initialQuery.limit);
	const [filters, setFilters] = useState<Record<string, string>>(
		initialQuery.filters
	);
	const [meta, setMeta] = useState<{ total: number; pages: number } | null>({
		total: 0,
		pages: 0,
	});
	const [sortField, setSortField] = useState<string | undefined>(
		initialQuery.sortField
	);
	const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC' | undefined>(
		initialQuery.sortOrder
	);

	// Garde page, tri et filtres dans l'adresse pour les retrouver au retour
	useEffect(() => {
		writeListQuery(queryKey, { page, limit, sortField, sortOrder, filters });
	}, [queryKey, page, limit, sortField, sortOrder, filters]);

	const token = localStorage.getItem('token');
	if (!token) throw new Error('Token manquant');

	// State export CSV
	const [isExporting, setIsExporting] = useState(false);

	// Export CSV : re-fetch de tous les éléments avec les filtres actifs, puis génération et téléchargement du fichier
	const handleExport = async () => {
		if (isExporting) return;
		setIsExporting(true);

		try {
			// Reconstruction des filtres actifs (même logique que le useEffect)
			const activeFilters: Record<string, string> = {};
			Object.entries(filters).forEach(([key, value]) => {
				if (!value) return;
				if (key.endsWith('_from') || key.endsWith('_to')) {
					const isFrom = key.endsWith('_from');
					const colIdx = key.replace(isFrom ? '_from' : '_to', '');
					const field = tableColumns[Number(colIdx)]?.field;
					if (field)
						activeFilters[`${field}${isFrom ? '_from' : '_to'}`] = value;
					return;
				}
				if (key.includes('.')) {
					activeFilters[key] = value;
				} else {
					const field = tableColumns[Number(key)]?.field;
					if (field) activeFilters[field] = value;
				}
			});

			// Récupération de tous les éléments (sans pagination)
			const result = await fetchData(token, {
				page: 1,
				limit: 9999,
				filters: activeFilters,
				sort: sortField ?? undefined,
				order: sortOrder ?? undefined,
			});

			const allItems: T[] =
				'data' in result && 'meta' in result ? result.data : (result as T[]);

			// Construction des lignes CSV (colonnes du tableau + colonnes export-only)
			const allColumns = [
				...tableColumns.map((col) => ({
					label: col.label,
					getValue: (item: T) => {
						if (col.exportValue) return col.exportValue(item);
						const rendered = col.render(item);
						if (typeof rendered === 'string' || typeof rendered === 'number')
							return rendered;
						return null;
					},
				})),
				...exportOnlyColumns.map((col) => ({
					label: col.label,
					getValue: (item: T) => col.exportValue(item),
				})),
			];

			const headers = allColumns.map((col) => `"${col.label}"`).join(';');

			const rows = allItems.map((item) =>
				allColumns
					.map((col) => {
						const val = col.getValue(item);
						return `"${String(val ?? '').replace(/"/g, '""')}"`;
					})
					.join(';')
			);

			// Génération et téléchargement du fichier CSV (encodage UTF-8 avec BOM pour Excel)
			const csvContent = '\uFEFF' + [headers, ...rows].join('\n');
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `${exportFileName ?? title}.csv`;
			link.click();
			URL.revokeObjectURL(url);
		} catch (err) {
			console.error("Erreur lors de l'export CSV", err);
		} finally {
			setIsExporting(false);
		}
	};

	useEffect(() => {
		setLoading(true);

		const activeFilters: Record<string, string> = {};

		Object.entries(filters).forEach(([key, value]) => {
			if (!value) return;

			// date from/to
			if (key.endsWith('_from') || key.endsWith('_to')) {
				const isFrom = key.endsWith('_from');
				const colIdx = key.replace(isFrom ? '_from' : '_to', '');
				const field = tableColumns[Number(colIdx)]?.field;
				if (field) {
					// pour les dates simples, on n'ajoute **pas de point**
					activeFilters[`${field}${isFrom ? '_from' : '_to'}`] = value;
				}
				return;
			}

			// Relation ou champ simple
			if (key.includes('.')) {
				activeFilters[key] = value; // **garde la clé brute pour le backend**
			} else {
				const field = tableColumns[Number(key)]?.field;
				if (field) activeFilters[field] = value;
			}
		});

		fetchData(token, {
			page,
			limit,
			filters: activeFilters,
			sort: sortField ?? undefined,
			order: sortOrder ?? undefined,
		})
			.then((res) => {
				if ('data' in res && 'meta' in res) {
					// mode paginé
					setItems(res.data);
					setMeta(res.meta);
				} else {
					// ancien mode
					setItems(res);
					setMeta(null);
				}
			})
			.finally(() => setLoading(false));
	}, [fetchData, token, page, limit, filters, sortField, sortOrder]);

	const handleAdd = () => navigate(getAddPath ? getAddPath() : '/app/');
	const handleEdit = (id: number) => {
		if (getEditPath) {
			navigate(getEditPath(id));
		}
	};
	const handleDelete = (id: number) => {
		deleteItem?.(id, token)
			.then(() => setItems((prev) => prev.filter((item) => item.id !== id)))
			.catch((err) => console.error(err));
	};
	const handleManage = (id: number) => {
		if (getManagePath) {
			navigate(getManagePath(id));
		}
	};
	const handleManageVersion = (id: number) => {
		if (getManageQuoteVersionPath) {
			/* on recherche la dernière version du devis correspondant et on navigue vers sa page de gestion */
			const item = items.find((i) => i.id === id) as
				| {
						quoteVersions?: QuoteVersion[];
						latestActiveQuoteVersion?: { id: number };
				  }
				| undefined;

			const latestActiveId = item?.latestActiveQuoteVersion?.id;
			if (latestActiveId) {
				navigate(getManageQuoteVersionPath(latestActiveId));
				return;
			}

			const quoteVersions = item?.quoteVersions;

			if (!Array.isArray(quoteVersions) || quoteVersions.length === 0) {
				console.warn("Aucune version de devis trouvée pour l'élément", id);
				return;
			}

			const getLatest = (versions: QuoteVersion[]) =>
				versions.reduce((prev, current) =>
					prev.versionIndex > current.versionIndex ? prev : current
				);

			/* 1) versions non annulées */
			const activeVersions = quoteVersions.filter(
				(v) => v.status !== 'CANCELED'
			);

			/* 2) fallback sur toutes les versions si nécessaire */
			const latestVersion =
				activeVersions.length > 0
					? getLatest(activeVersions)
					: getLatest(quoteVersions);

			navigate(getManageQuoteVersionPath(latestVersion.id));
		}
	};

	const actions: Action<T>[] = [
		...(getManageQuoteVersionPath
			? [
					{
						type: 'manage',
						label: 'Gérer',
						onClick: (item: T) => handleManageVersion(item.id),
					} as Action<T>,
				]
			: []),
		...(getManageItemPath
			? [
					{
						type: 'manage',
						label: 'Gérer',
						onClick: (item: T) => navigate(getManageItemPath(item)),
					} as Action<T>,
				]
			: []),
		...(getManagePath
			? [
					{
						type: 'manage',
						label: 'Gérer',
						onClick: (item: T) => handleManage(item.id),
					} as Action<T>,
				]
			: []),
		...(getEditPath
			? [
					{
						type: 'edit',
						label: 'Éditer',
						onClick: (item: T) => handleEdit(item.id),
					} as Action<T>,
				]
			: []),
		...(deleteItem
			? [
					{
						type: 'delete',
						label: 'Supprimer',
						onClick: (item: T) => handleDelete(item.id),
						confirmMessage: (item: T) =>
							`Voulez-vous vraiment supprimer ${item.label || item.name || 'cet élément'} ?`,
					} as Action<T>,
				]
			: []),
		...extraActions,
	];

	return (
		<div
			className={`max-w-6xl mx-auto rounded-lg p-6 
				${
					size == 'xs'
						? 'bg-gray-50 shadow-[rgba(0,0,0,0.1)_0px_-2px_6px_-2px,rgba(0,0,0,0.1)_0px_4px_6px_-1px]'
						: 'bg-white shadow-md'
				}
				${className}
			`}
		>
			{/* HEADER DE LA CARD */}
			<div
				className={`flex mb-3 items-center
							${size == 'sm' ? 'text-gray-700' : ''}
							${size == 'xs' ? 'text-gray-800' : ''}`}
			>
				{size === 'xs' ? (
					<>
						{showIcon && icon && (
							<FontAwesomeIcon icon={icon} className="w-4 h-4 mr-2 ml-2" />
						)}
						<h2 className={`font-bold text-lg`}>{title}</h2>
					</>
				) : (
					<>
						{showIcon && icon && (
							<FontAwesomeIcon icon={icon} className="w-6 h-6 mr-2" />
						)}
						<h1
							className={`font-bold ${size == 'sm' ? 'text-2xl' : 'text-2xl'}`}
						>
							{title}
						</h1>
					</>
				)}

				{getAddPath && (
					<div className="ml-auto mr-2">
						<AddButton
							label={size !== 'sm' ? addButtonLabel : ''}
							onClick={handleAdd}
							size={size === 'lg' ? 'lg' : size === 'md' ? 'md' : 'sm'}
						/>
					</div>
				)}

				{/* Icône d'export CSV (visible si exportable) */}
				{exportable && (
					<div className={getAddPath ? '' : 'ml-auto'}>
						<button
							onClick={handleExport}
							disabled={isExporting}
							title="Exporter en CSV"
							className="flex items-center justify-center w-9 h-9 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
						>
							<FontAwesomeIcon
								icon={isExporting ? faSpinner : faFileExcel}
								spin={isExporting}
								className="w-8 h-8"
							/>
						</button>
					</div>
				)}
			</div>

			{/* TABLEAU DES ÉLÉMENTS */}
			<GenericTable
				items={items}
				columns={tableColumns}
				actions={actions}
				filters={filters}
				onFiltersChange={(newFilters) => {
					setFilters(newFilters); // déclenche useEffect pour fetch
					setPage(1);
				}}
				onSortChange={(field, order) => {
					setSortField(field);
					setSortOrder(order);
					setPage(1);
				}}
				sortField={sortField}
				sortOrder={sortOrder}
				emptyMessage={emptyMessage}
				loading={loading}
				size={size === 'lg' || size === 'md' ? 'md' : 'sm'}
			/>

			{meta && (
				<div className="flex flex-wrap justify-between items-center mt-4 gap-3">
					{/* Pagination centrée avec boutons encadrants */}
					<div className="flex items-center gap-2 justify-center flex-1">
						<SecondaryButton
							label="Précédent"
							onClick={() => setPage((p) => p - 1)}
							size="sm"
							disabled={page <= 1}
							icon={faArrowLeft}
						/>

						<span className="text-sm text-gray-500 font-bold px-2">
							Page {page} / {meta.pages}
							<span className="text-gray-500 font-normal ml-2">
								({meta.total} éléments)
							</span>
						</span>

						<SecondaryButton
							label="Suivant"
							onClick={() => setPage((p) => p + 1)}
							size="sm"
							disabled={page >= meta.pages}
							icon={faArrowRight}
						/>
					</div>

					{/* Select limit à droite */}
					<div>
						<select
							value={limit}
							onChange={(e) => {
								setLimit(Number(e.target.value));
								setPage(1);
							}}
							className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
						>
							<option value={10}>10</option>
							<option value={25}>25</option>
							<option value={50}>50</option>
							<option value={100}>100</option>
						</select>
					</div>
				</div>
			)}

			{/* BOUTON RETOUR — affiché uniquement si getReturnPath est fourni */}
			{getReturnPath && (
				<div className="flex justify-end mt-6">
					<SecondaryButton
						label={returnLabel}
						onClick={() => navigate(getReturnPath())}
						size="md"
						icon={returnIcon}
						invert={returnInvert}
					/>
				</div>
			)}
		</div>
	);
}

export default GenericCrudCard;
